import { useEffect, useState, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { subscribe, toast } from "./toast";
import "./toast.css";

/* ---------- Íconos inline (sin librería) ---------- */
const ICONS = {
  success: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" /><path d="m8.5 12.5 2.5 2.5 4.5-5" />
    </svg>
  ),
  error: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" /><path d="M12 7.5v5" /><path d="M12 16.5h.01" />
    </svg>
  ),
  warning: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.3 3.9 2 18a2 2 0 0 0 1.7 3h16.6A2 2 0 0 0 22 18L13.7 3.9a2 2 0 0 0-3.4 0Z" />
      <path d="M12 9v4" /><path d="M12 17h.01" />
    </svg>
  ),
  info: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" /><path d="M12 11v5" /><path d="M12 8h.01" />
    </svg>
  ),
};
const IconClose = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 6l12 12M18 6 6 18" />
  </svg>
);
const IconChevron = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m6 9 6 6 6-6" />
  </svg>
);

const EXIT_MS = 220;
const resolveIcon = (data) => {
  if (data.icon === null || data.icon === false) return null;
  return data.icon ? data.icon : ICONS[data.type] || ICONS.info;
};

/* =====================================================================
   Toast simple (texto corto, auto-cierre, pausa al pasar el mouse)
   ===================================================================== */
function SimpleToastItem({ data }) {
  const [leaving, setLeaving] = useState(false);
  const timerRef = useRef(null);
  const remainingRef = useRef(data.duration);
  const startRef = useRef(0);

  const close = useCallback(() => {
    setLeaving(true);
    setTimeout(() => toast.dismiss(data.id), EXIT_MS);
  }, [data.id]);

  const startTimer = useCallback(() => {
    if (!data.duration || data.duration <= 0) return;
    startRef.current = Date.now();
    timerRef.current = setTimeout(close, remainingRef.current);
  }, [data.duration, close]);

  const pauseTimer = useCallback(() => {
    if (!timerRef.current) return;
    clearTimeout(timerRef.current);
    remainingRef.current -= Date.now() - startRef.current;
  }, []);

  useEffect(() => {
    startTimer();
    return () => clearTimeout(timerRef.current);
  }, [startTimer]);

  const icon = resolveIcon(data);
  const autoCloses = data.duration && data.duration > 0;

  return (
    <div
      className={`tst-toast tst-${data.type}` + (leaving ? " tst-leaving" : "") + (data.className ? ` ${data.className}` : "")}
      role={data.type === "error" ? "alert" : "status"}
      onMouseEnter={pauseTimer}
      onMouseLeave={startTimer}
    >
      {icon && <span className="tst-icon">{icon}</span>}
      <div className="tst-body">
        {data.title && <p className="tst-title">{data.title}</p>}
        {data.message && <p className="tst-msg">{data.message}</p>}
        {data.action && (
          <button type="button" className="tst-action" onClick={() => { data.action.onClick?.(); close(); }}>
            {data.action.label}
          </button>
        )}
      </div>
      {data.dismissible && (
        <button type="button" className="tst-close" aria-label="Cerrar" onClick={close}>{IconClose}</button>
      )}
      {data.showProgress && autoCloses && (
        <span className="tst-progress" style={{ animationDuration: `${data.duration}ms` }} />
      )}
    </div>
  );
}

/* =====================================================================
   Toast especial (expandible + cuenta regresiva visible + "clic para detener")
   ===================================================================== */
function RichToastItem({ data }) {
  const [leaving, setLeaving] = useState(false);
  const [expanded, setExpanded] = useState(!!data.defaultExpanded);
  const [paused, setPaused] = useState(false);
  const [remaining, setRemaining] = useState(data.duration || 0);
  const lastRef = useRef(Date.now());

  const isCountdown = !!data.countdown && data.duration > 0;

  const close = useCallback(() => {
    setLeaving(true);
    setTimeout(() => toast.dismiss(data.id), EXIT_MS);
  }, [data.id]);

  // Cuenta regresiva con tick (permite número vivo + pausa precisa)
  useEffect(() => {
    if (!isCountdown || paused) return;
    lastRef.current = Date.now();
    const id = setInterval(() => {
      const now = Date.now();
      const dt = now - lastRef.current;
      lastRef.current = now;
      setRemaining((r) => Math.max(0, r - dt));
    }, 100);
    return () => clearInterval(id);
  }, [isCountdown, paused]);

  // Cierra al llegar a cero
  useEffect(() => {
    if (isCountdown && remaining <= 0) close();
  }, [isCountdown, remaining, close]);

  // Auto-cierre para tarjetas ricas SIN cuenta regresiva
  useEffect(() => {
    if (isCountdown || !data.duration || data.duration <= 0) return;
    const id = setTimeout(close, data.duration);
    return () => clearTimeout(id);
  }, [isCountdown, data.duration, close]);

  const icon = resolveIcon(data);
  const hasDetails = data.expandable && (data.details || data.action);
  const secondsLeft = Math.ceil(remaining / 1000);
  const elapsedRatio = data.duration ? (data.duration - remaining) / data.duration : 0;

  return (
    <div
      className={`tst-toast tst-rich tst-${data.type}` + (leaving ? " tst-leaving" : "") + (data.className ? ` ${data.className}` : "")}
      role={data.type === "error" ? "alert" : "status"}
    >
      {/* cabecera */}
      <div className="tst-head">
        {icon && <span className="tst-icon">{icon}</span>}
        <p className="tst-title">{data.title || data.message}</p>
        <div className="tst-tools">
          {hasDetails && (
            <button
              type="button"
              className={"tst-chevron" + (expanded ? " tst-open" : "")}
              aria-expanded={expanded}
              aria-label={expanded ? "Contraer" : "Expandir"}
              onClick={() => setExpanded((e) => !e)}
            >
              {IconChevron}
            </button>
          )}
          {data.dismissible && (
            <button type="button" className="tst-close" aria-label="Cerrar" onClick={close}>{IconClose}</button>
          )}
        </div>
      </div>

      {/* detalle expandible */}
      {hasDetails && (
        <div className={"tst-details-wrap" + (expanded ? " tst-open" : "")}>
          <div className="tst-details-inner">
            {data.details && <p className="tst-details">{data.details}</p>}
            {data.action && (
              <button type="button" className="tst-okay" onClick={() => { data.action.onClick?.(); close(); }}>
                {data.action.label}
              </button>
            )}
          </div>
        </div>
      )}

      {/* pie con cuenta regresiva */}
      {isCountdown && (
        <div className="tst-countdown">
          <span>
            Este mensaje se cerrará en <b>{secondsLeft}</b> {secondsLeft === 1 ? "segundo" : "segundos"}.{" "}
          </span>
          <button type="button" className="tst-stop" onClick={() => setPaused((p) => !p)}>
            {paused ? "Clic para reanudar." : "Clic para detener."}
          </button>
        </div>
      )}

      {/* barra que crece con el tiempo transcurrido */}
      {isCountdown && data.showProgress && (
        <span className="tst-progress tst-progress-grow" style={{ transform: `scaleX(${elapsedRatio})` }} />
      )}
    </div>
  );
}

/* =====================================================================
   Modal de confirmación (diálogo centrado con backdrop)
   Se abre con toast.confirm(...) -> resuelve Promise<boolean>.
   Tipos: success | error | warning | info (colorean ícono y botón).
   ===================================================================== */
function ModalToastItem({ data }) {
  const [leaving, setLeaving] = useState(false);
  const cancelRef = useRef(null);

  const close = useCallback((confirmed) => {
    setLeaving(true);
    setTimeout(() => {
      if (confirmed) data.onConfirm?.();
      else data.onCancel?.();
      toast.dismiss(data.id);
    }, EXIT_MS);
  }, [data]);

  // ESC = cancelar, bloquea el scroll del body y da foco inicial al botón Cancelar
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && data.dismissible !== false) close(false); };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    cancelRef.current?.focus();
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [close, data.dismissible]);

  const icon = resolveIcon(data);
  const showCancel = data.cancelLabel !== null && data.cancelLabel !== false;

  return (
    <div
      className={"tst-modal-overlay" + (leaving ? " tst-leaving" : "")}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && data.dismissible !== false && data.dismissOnBackdrop !== false) close(false);
      }}
    >
      <div
        className={`tst-modal tst-${data.type}` + (data.className ? ` ${data.className}` : "")}
        role="alertdialog"
        aria-modal="true"
        aria-label={data.title || undefined}
      >
        {icon && <span className="tst-modal-icon">{icon}</span>}
        {data.title && <h2 className="tst-modal-title">{data.title}</h2>}
        {data.message && <p className="tst-modal-msg">{data.message}</p>}
        <div className="tst-modal-actions">
          {showCancel && (
            <button ref={cancelRef} type="button" className="tst-modal-cancel" onClick={() => close(false)}>
              {data.cancelLabel || "Cancelar"}
            </button>
          )}
          <button type="button" className="tst-modal-confirm" onClick={() => close(true)}>
            {data.confirmLabel || "Aceptar"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* =====================================================================
   Contenedor: montar UNA sola vez en el root de la app
   ===================================================================== */
export default function ToastContainer() {
  const [items, setItems] = useState([]);

  useEffect(() => subscribe(setItems), []);

  if (typeof document === "undefined") return null;

  // Los modales van aparte (centrados, con backdrop); el resto por posición.
  const modals = items.filter((t) => t.variant === "modal");
  const positioned = items.filter((t) => t.variant !== "modal");

  const groups = positioned.reduce((acc, t) => {
    (acc[t.position] = acc[t.position] || []).push(t);
    return acc;
  }, {});

  return createPortal(
    <>
      {Object.entries(groups).map(([position, group]) => (
        <div key={position} className={`tst-viewport tst-${position}`} aria-live="polite" role="region">
          {group.map((t) =>
            t.expandable || t.countdown
              ? <RichToastItem key={t.id} data={t} />
              : <SimpleToastItem key={t.id} data={t} />
          )}
        </div>
      ))}
      {modals.map((t) => <ModalToastItem key={t.id} data={t} />)}
    </>,
    document.body
  );
}